import * as THREE from 'three';
import { bodyProfile, canopyProfile, extrudeProfile, BODY_WIDTH, GLASS_WIDTH } from '@/entities/CarView';

/**
 * TackleGhost.ts — the SLIDE TACKLE's ghost car (M14): a translucent, additive
 * copy of the firer's sports car that races up the course, then throws itself
 * sideways in a proper soccer slide — yaw swings ~90°, the body drops and
 * heels over, and it dissolves as it follows through. Purely cosmetic — the
 * race owns the chase/hit logic and feeds this view a pose + slide phase.
 */
export class TackleGhost {
  readonly group = new THREE.Group();
  private mat: THREE.MeshBasicMaterial;
  private inner = new THREE.Group();

  constructor() {
    this.mat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.45,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const hull = new THREE.Mesh(extrudeProfile(bodyProfile(), BODY_WIDTH), this.mat);
    const canopy = new THREE.Mesh(extrudeProfile(canopyProfile(), GLASS_WIDTH), this.mat);
    this.inner.add(hull, canopy);
    this.group.add(this.inner);
    this.group.visible = false;
  }

  /** Show the ghost in the firer's colour at the launch point. */
  launch(color: number): void {
    this.mat.color.set(color);
    this.mat.opacity = 0.45;
    this.group.visible = true;
    this.inner.rotation.set(0, 0, 0);
  }

  /**
   * Place the ghost. `slide01` is 0 while chasing and ramps 0→1 through the
   * tackle: the body swings sideways, heels over, and fades out.
   */
  update(x: number, y: number, z: number, heading: number, slide01: number): void {
    this.group.position.set(x, y, z);
    this.group.rotation.y = heading;
    const s = Math.min(1, Math.max(0, slide01));
    const ease = 1 - (1 - s) * (1 - s); // fast in, settling out
    this.inner.rotation.y = ease * (Math.PI / 2) * 0.96; // the sideways swing
    this.inner.rotation.z = ease * 0.34; // heeling onto its side
    this.inner.position.y = -ease * 0.18; // dropping low into the slide
    this.mat.opacity = 0.45 * (1 - s * 0.85); // dissolving through the follow-through
  }

  hide(): void {
    this.group.visible = false;
  }

  dispose(): void {
    this.mat.dispose();
    this.inner.children.forEach((c) => (c as THREE.Mesh).geometry?.dispose());
  }
}
